import { addWeeks, endOfWeek, startOfWeek } from 'date-fns';
import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { NextCoachingInsightService } from '../../coaching-base/coaching-insight.service';
import { Dependencies } from '../../store';
import { pluck } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { CoverageDataDto } from '../coverage.coaching-insight.model';
import { CoverageCoachingInsightService } from '../coverage.coaching-insight.service';
import { ScheduleEventDto } from '../schedule.coaching-insight.model';
import { ScheduleCoachingInsightService } from '../schedule.coaching-insight.service';

import { RosterItem } from './coaching-coverage-grid.model';
import {
    ActionType,
    RootReloadAction,
    rootReloadResult,
} from './coaching-coverage.action';
import { MAX_SHOWABLE_RANGE } from './coaching-coverage.model';
import { RootState } from './coaching-coverage.state';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootReload = (
    action: ActionsObservable<PayloadAction<ActionType, RootReloadAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COVERAGE__ROOT__RELOAD),
        switchMap((act: PayloadAction<ActionType, RootReloadAction>) => {
            const rootState = pluck<RootState>(state.value, act.storePath);

            const coverageService =
                deps.injector.get<CoverageCoachingInsightService>(
                    CoverageCoachingInsightService,
                );
            const scheduleService =
                deps.injector.get<ScheduleCoachingInsightService>(
                    ScheduleCoachingInsightService,
                );
            const coachingInsightService =
                deps.injector.get<NextCoachingInsightService>(
                    NextCoachingInsightService,
                );

            const start = startOfWeek(act.payload.context.periodDate);
            const end = endOfWeek(
                addWeeks(
                    act.payload.context.periodDate,
                    MAX_SHOWABLE_RANGE - 1,
                ),
            );
            const rosterType =
                act.payload.rosterType != null
                    ? act.payload.rosterType
                    : rootState.rosterType;

            const obses: [
                Observable<CoverageDataDto>,
                Observable<ScheduleEventDto[]>,
                Observable<RosterItem[]>,
            ] = [
                of(null).pipe(
                    coverageService.retrieve(() => ({
                        performerId: act.payload.context.user.id,
                        periodTypeId: act.payload.context.periodType,
                        periodDate: act.payload.context.periodDate,
                        rangeTypeId: rootState.periodType,
                        periodStart: start,
                        periodEnd: end,
                    })),
                ),
                of(null).pipe(
                    scheduleService.listEvents(() => ({
                        performerId: act.payload.context.user.id,
                        periodTypeId: rootState.periodType,
                        periodStart: start,
                        periodEnd: end,
                    })),
                ),
                coachingInsightService.listRoster(act.payload.context.user.id),
            ];

            return forkJoin(obses).pipe(
                map((ret) =>
                    rootReloadResult(
                        {
                            ...act.payload,
                            rosterType,
                            start,
                            end,
                            coverage: ret[0],
                            scheduleEvents: ret[1],
                            roster: ret[2],
                        },
                        act.storePath,
                    ),
                ),
                catchError((e) => {
                    console.log(e);
                    return of(
                        rootReloadResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

export default [rootReload];
